﻿import {DateOnly, NamedValue, TimeOnly} from "@2d-front-monorepo/core";
import ParkingPrebookingAvailabilityType from "../enums/ParkingPrebookingAvailabilityType";
import PaymentMethod from "../enums/PaymentMethod";
import {TariffId} from "../nominalTypes/TariffId";
import {ZoneId} from "../nominalTypes/ZoneId";
import {GetParkingOperationStepsArguments} from "../types/arguments/GetParkingOperationStepsArguments";
import {
	ParkingPrebookingAvailabilityEntry,
	SelectedParkingPrebookingAvailabilityEntry
} from "../types/ParkingPrebookingAvailabilityEntry";
import {MultiOperationPermit} from "./MultiOperationPermit";
import {MultiOperationSegment} from "./MultiOperationSegment";
import {PrebookingTimes} from "./PrebookingTimes";

export interface RecurrentPermitOptions {
	minDate: DateOnly;
	maxDate: DateOnly;

	maxConsecutiveDays: number;
}

export const enum ModifiedProperty {
	All,
	StartDate,
	EndDate,
	StartTime,
	EndTime,
	DayOfWeek
}

export abstract class BasePermitRecurrence {
	protected readonly _minDate: DateOnly;
	protected readonly _maxDate: DateOnly;
	protected readonly _maxConsecutiveDays: number;
	protected _operations: MultiOperationPermit;
	//contains a map where the entry key is the date in ISO format and the value is the list of available times for that date. The available property of the value indicates whether the date is available for prebooking.
	private readonly _dateMap: Map<string, PrebookingTimes>;
	protected readonly _unavailable: Set<string>;

	protected _startDate: DateOnly;
	protected _endDate: DateOnly;
	protected _startTime: TimeOnly;
	protected _endTime: TimeOnly;
	protected _availability: ParkingPrebookingAvailabilityEntry[];

	protected constructor(options: RecurrentPermitOptions) {
		this._minDate = options.minDate;
		this._maxDate = options.maxDate;
		this._startDate = this._endDate = DateOnly.today();
		this._startTime = TimeOnly.zero();
		this._endTime = this._startTime.addHours(1);
		this._maxConsecutiveDays = options.maxConsecutiveDays;
		this._dateMap = new Map();
		this._unavailable = new Set();
		this._availability = [];
		this._operations = this._createMultiOperations();
	}

	public setAvailability(availability: ParkingPrebookingAvailabilityEntry[]): void {
		//everything here works by assuming that the availability is sorted by startDate.
		this._availability = availability.sort((a, b) =>
			a.startDate.compareTo(b.startDate)
		);

		this._updateDateMaps();
	}

	public dayClassName(date: DateOnly): string | undefined {
		if (this._isUnavailable(date)) {
			return undefined;
		}

		const e = this._getPrebookingTimes(date);
		return e == null
			? undefined
			: e.times.length === 24 ? undefined : "calendar__cell--holed";
	}

	public get startDate(): DateOnly {
		return this._startDate;
	}

	public set startDate(startDate: DateOnly) {
		this._startDate = this._clamp(startDate);
		this._createSegments(ModifiedProperty.StartDate);
	}

	public get endDate(): DateOnly {
		return this._endDate;
	}

	public set endDate(endDate: DateOnly) {
		this._endDate = this._clamp(endDate);
		this._createSegments(ModifiedProperty.EndDate);
	}

	public get startTime(): TimeOnly {
		return this._startTime;
	}

	public set startTime(startTime: TimeOnly) {
		this._startTime = startTime;
		const prebookingTimes = this._getPrebookingTimes(this._startDate);
		const end = prebookingTimes?.times.find(x => x.start.equals(startTime));
		this._endTime = end?.end ?? startTime;
		this._createSegments(ModifiedProperty.StartTime);
	}

	public get endTime(): TimeOnly {
		return this._endTime;
	}

	public set endTime(endTime: TimeOnly) {
		this._endTime = endTime;
		this._createSegments(ModifiedProperty.EndTime);
	}

	public isValidStartDate(): boolean {
		return !this._isDateInvalid(this._startDate);
	}

	public isValidEndDate(): boolean {
		if (this._isDateInvalid(this._endDate)) {
			return false;
		}

		return !(this._startDate.compareTo(this._endDate) > 0 && this._startTime.compareTo(this._endTime) >= 0);
	}

	public isValidStartTime(): boolean {
		const startTimes = this.getAvailableStartTimes();
		return startTimes.find(x => x.value.equals(this._startTime)) != null;
	}

	public isValidEndTime(): boolean {
		if (this._startDate.compareTo(this._endDate) >= 0 && this._startTime.compareTo(this._endTime) >= 0) {
			return false;
		}

		const endTimes = this.getAvailableEndTimes();
		return endTimes.find(x => x.value.equals(this._endTime)) != null;
	}

	public abstract getAvailableStartTimes(): NamedValue<TimeOnly>[];

	public abstract getAvailableEndTimes(): NamedValue<TimeOnly>[];

	public isValid(): boolean {
		return !(this._startDate.compareTo(this._endDate) >= 0 && this._startTime.compareTo(this._endTime) >= 0);
	}

	public getSelected(): SelectedParkingPrebookingAvailabilityEntry[] {
		const result: SelectedParkingPrebookingAvailabilityEntry[] = [];

		for (const op of this._operations) {
			if (op.startDate.toDateOnly().equals(op.endDate.toDateOnly())) {
				result.push(op.toPrebookingEntry());

			} else {
				result.push(...this._getSpans(op))
			}
		}

		return result;
	}

	public getParkingOperationArguments(tariffId: TariffId, zoneId: ZoneId, plates: string[][]): GetParkingOperationStepsArguments {
		const selected = this.getSelected();
		if (selected.length === 0) {
			throw new Error("getParkingOperationArguments requires a non-empty selection: check isValid() first");
		}
		return {
			startDate: selected[0].startDate,
			endDate: selected.at(-1)!.endDate,
			tariffId: tariffId,
			branch: {zoneId: zoneId},
			deferConfirmation: true,
			licensePlates: plates,
			paymentMethod: PaymentMethod.Undefined,
			preBookingEntries: this._availability,
			selectedPreBookingEntries: selected
		}
	}

	protected abstract _createSegments(property: ModifiedProperty): void;

	protected _isUnavailable(date: DateOnly): boolean {
		return this._unavailable.has(date.toJSON());
	}

	protected _isDateInvalid(date: DateOnly): boolean {
		return date < this._minDate || date > this._maxDate || this._isUnavailable(date);
	}

	protected _getPrebookingTimes(date: DateOnly): PrebookingTimes | undefined {
		return this._dateMap.get(date.toJSON());
	}

	private _clamp(value: DateOnly): DateOnly {
		return value < this._minDate ? this._minDate : value > this._maxDate ? this._maxDate : value;
	}

	private _getSpans(op: MultiOperationSegment): SelectedParkingPrebookingAvailabilityEntry[] {
		const spans: SelectedParkingPrebookingAvailabilityEntry[] = [];

		let currentStart = op.startDate;
		const endDate = op.endDate;
		const firstSpanEnd = op.startDate.startOfDay().addDays(1);

		//first span
		spans.push({
			startDate: currentStart,
			endDate: firstSpanEnd
		})

		currentStart = firstSpanEnd;
		while (currentStart < endDate) {
			//complete days
			const spanEnd = currentStart.addDays(1);
			if (spanEnd > endDate) break;

			spans.push({
				startDate: currentStart,
				endDate: spanEnd
			});

			currentStart = spanEnd;
		}

		//last span (if any)
		if (currentStart < endDate) {
			spans.push({
				startDate: currentStart,
				endDate: endDate
			});
		}

		return spans;
	}

	private _createMultiOperations(): MultiOperationPermit {
		return new MultiOperationPermit(this._availability, this._unavailable);
	}

	private _updateDateMaps(): void {
		const dateMap = this._dateMap;
		dateMap.clear();

		for (const entry of this._availability) {
			let start = entry.startDate;
			const end = entry.endDate;
			const isAvailable = entry.type === ParkingPrebookingAvailabilityType.Available;
			const key = start.toDateOnlyString();
			let times = dateMap.get(key);

			if (times == null) {
				//first time a date is found in the map...
				times = {times: [], available: isAvailable};
				dateMap.set(key, times);

			} else {
				times.available ||= isAvailable;
			}

			if (isAvailable) {
				times.times.push({
					start: start.toTimeOnly(),
					end: end.toTimeOnly()
				});
			}
		}

		const unavailable = this._unavailable;
		unavailable.clear();

		const allDates = new Set<string>();
		const datesWithNonNotAvailable = new Set<string>();

		for (const entry of this._availability) {
			const key = entry.startDate.toDateOnlyString();
			allDates.add(key);

			if (entry.type !== ParkingPrebookingAvailabilityType.NotAvailable) {
				datesWithNonNotAvailable.add(key);
			}
		}

		for (const date of allDates) {
			if (!datesWithNonNotAvailable.has(date)) {
				unavailable.add(date);
			}
		}

		this._operations = this._createMultiOperations();
	}
}
